import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Upload, X, Loader2, ShieldCheck, Clock, CreditCard, Check, Zap, HelpCircle, Download, Heart } from "lucide-react";
import { api, imgUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AuthModal from "@/components/AuthModal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { toast } from "sonner";

const CATEGORIES = ["Face", "Eyes", "Lips", "Fragrance", "Skincare", "Tools"];
const HYGIENE_PROTOCOL_URL = "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/866e55ff8d7efb52cd119518c1e4581e82dc6ff764e6b18e1a19f1c8e92c07ea.jpeg";

export default function Sell() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  usePageMeta(
    "Sell Your Makeup — Become a Verified Seller | Beauty Trade Club",
    "Turn your new and gently-used beauty products into cash. Apply to become a verified seller on Beauty Trade Club, list with photos and get paid securely via Stripe.",
    "/sell"
  );
  const [authOpen, setAuthOpen] = useState(false);
  const [form, setForm] = useState({ title: "", brand: "", description: "", price: "", sale_price: "", condition: "", category: "", times_used: "" });
  const [images, setImages] = useState([]);
  const [skipPhoto, setSkipPhoto] = useState({ front: false, label: false, product: false });
  const [published, setPublished] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [connect, setConnect] = useState(null);
  const [params] = useSearchParams();
  const [sellerForm, setSellerForm] = useState({ legal_name: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", zip_code: "", country: "United States" });
  const [applying, setApplying] = useState(false);
  const setS = (k, v) => setSellerForm((f) => ({ ...f, [k]: v }));
  const [credits, setCredits] = useState(0);
  const [enhance, setEnhance] = useState(null); // { index, original, enhanced, style, loading }
  const [buyingCredits, setBuyingCredits] = useState(false);
  const [boostAll, setBoostAll] = useState(null); // { total, done } while running
  const [showConditionHelp, setShowConditionHelp] = useState(false);

  useEffect(() => {
    if (user && (user.seller_status === "approved" || user.role === "admin")) {
      api.get("/seller/connect/status").then(({ data }) => setConnect(data)).catch(() => {});
      api.get("/seller/credits").then(({ data }) => setCredits(data.ai_credits)).catch(() => {});
      const cs = params.get("credits_session");
      if (cs) {
        api.get(`/seller/credits/status/${cs}`).then(({ data }) => {
          setCredits(data.ai_credits);
          toast.success("Photo boosts added! You can now boost your photos. ⚡");
        }).catch(() => {});
      }
    }
  }, [user, params]);

  const buyCredits = async () => {
    setBuyingCredits(true);
    try {
      const { data } = await api.post("/seller/credits/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); setBuyingCredits(false); }
  };

  const runBoost = async (index) => {
    setEnhance({ index, original: images[index], enhanced: null, loading: true });
    try {
      const { data } = await api.post("/seller/enhance-image", { image_path: images[index] });
      setEnhance((e) => (e ? { ...e, enhanced: data.path, loading: false } : null));
    } catch (err) {
      toast.error(apiError(err));
      setEnhance((e) => (e ? { ...e, loading: false } : null));
    }
  };

  const openEnhance = (index) => {
    if (credits < 1) { toast.error("You need photo boosts to enhance photos. Buy a boost pack first."); return; }
    runBoost(index);
  };

  const keepEnhanced = async () => {
    try {
      const { data } = await api.post("/seller/enhance-image/confirm");
      setCredits(data.ai_credits);
      setImages((imgs) => imgs.map((img, i) => (i === enhance.index ? enhance.enhanced : img)));
      toast.success("Boosted photo applied! ⚡");
      setEnhance(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const runBoostAll = async () => {
    const n = Math.min(credits, images.length);
    if (n < 1) { toast.error("You need photo boosts first. Buy a boost pack to continue."); return; }
    if (!window.confirm(`Boost ${n} photo${n === 1 ? "" : "s"} using ${n} boost${n === 1 ? "" : "s"}? Any unused boosts stay on your account for your next listing.`)) return;
    setBoostAll({ total: n, done: 0 });
    const next = [...images];
    let done = 0;
    for (let i = 0; i < next.length && done < n; i++) {
      try {
        const { data } = await api.post("/seller/enhance-image", { image_path: next[i] });
        await api.post("/seller/enhance-image/confirm");
        next[i] = data.path;
        done += 1;
        setImages([...next]);
        setBoostAll({ total: n, done });
      } catch (e) { /* skip photos that fail, keep going */ }
    }
    try { const { data } = await api.get("/seller/credits"); setCredits(data.ai_credits); } catch (e) {}
    setBoostAll(null);
    toast.success(done ? `Boosted ${done} photo${done === 1 ? "" : "s"}! ⚡ Unused boosts are saved for next time.` : "No photos were boosted — please try again.");
  };

  const onboard = async () => {
    try {
      const { data } = await api.post("/seller/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.path]);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (images.length === 0) { toast.error("Please upload at least one image"); return; }
    if (!form.condition) { toast.error("Please choose a condition — New, Swatched once, or Lightly used"); return; }
    if (!form.times_used) { toast.error("Please tell buyers how many times this has been used"); return; }
    if (!form.category) { toast.error("Please choose a category"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        sale_price: form.sale_price ? parseFloat(form.sale_price) : null,
        images,
      };
      const { data } = await api.post("/products", payload);
      toast.success("Listing published!");
      setPublished(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const applySeller = async () => {
    const req = ["legal_name", "phone", "address_line1", "city", "state", "zip_code"];
    for (const k of req) {
      if (!sellerForm[k].trim()) { toast.error("Please fill in all required fields"); return; }
    }
    setApplying(true);
    try {
      await api.post("/seller/apply", sellerForm);
      await refreshUser();
      toast.success("You're verified! Start listing your beauty now. ✨");
    } catch (err) {
      toast.error(apiError(err));
    } finally { setApplying(false); }
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-bold mb-4">Turn your beauty stash into cash 💸</h1>
        <p className="text-neutral-500 mb-6">Sign in to list the makeup you're not reaching for — you set the price, you keep 85%.</p>
        <button data-testid="sell-signin-btn" onClick={() => setAuthOpen(true)} className="bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-8 py-3 btn-glow transition-all">
          Sign In to Start Selling
        </button>
        <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      </div>
    );
  }

  const status = user.seller_status;
  if (status !== "approved" && user.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <ShieldCheck size={48} className="text-[#E8956B] mx-auto mb-4" />
        <h1 className="font-display text-3xl font-bold mb-3">Become a Verified Seller ✨</h1>
        {status === "pending" ? (
          <div data-testid="seller-pending" className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-5 text-yellow-200 flex items-center justify-center gap-2">
            <Clock size={18} /> Your seller application is under review. Check back soon!
          </div>
        ) : status === "rejected" ? (
          <p className="text-red-400" data-testid="seller-rejected">Your application was not approved. Please contact support.</p>
        ) : (
          <>
            <p className="text-neutral-500 mb-6 max-w-lg mx-auto">
              You're one quick step away from turning unused beauty into cash. As a trusted marketplace, we collect a
              few details so every sale is safe and above-board — get verified instantly and start listing today.
              Your payout banking &amp; tax info is handled separately &amp; securely by Stripe.
            </p>
            <div className="max-w-lg mx-auto text-left space-y-3">
              <div>
                <label className="text-xs font-bold text-neutral-500">Full legal name *</label>
                <Input data-testid="seller-legal-name" value={sellerForm.legal_name} onChange={(e) => setS("legal_name", e.target.value)} placeholder="Jane A. Doe" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-neutral-500">Phone number *</label>
                  <Input data-testid="seller-phone" value={sellerForm.phone} onChange={(e) => setS("phone", e.target.value)} placeholder="(555) 123-4567" className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-bold text-neutral-500">Email</label>
                  <Input value={user.email} disabled className="mt-1 opacity-70" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-neutral-500">Street address *</label>
                <Input data-testid="seller-address1" value={sellerForm.address_line1} onChange={(e) => setS("address_line1", e.target.value)} placeholder="123 Beauty Lane" className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-bold text-neutral-500">Apt / Suite (optional)</label>
                <Input data-testid="seller-address2" value={sellerForm.address_line2} onChange={(e) => setS("address_line2", e.target.value)} placeholder="Apt 4B" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-neutral-500">City *</label>
                  <Input data-testid="seller-city" value={sellerForm.city} onChange={(e) => setS("city", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-bold text-neutral-500">State / Province *</label>
                  <Input data-testid="seller-state" value={sellerForm.state} onChange={(e) => setS("state", e.target.value)} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-neutral-500">ZIP / Postal code *</label>
                  <Input data-testid="seller-zip" value={sellerForm.zip_code} onChange={(e) => setS("zip_code", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-bold text-neutral-500">Country *</label>
                  <Input data-testid="seller-country" value={sellerForm.country} onChange={(e) => setS("country", e.target.value)} className="mt-1" />
                </div>
              </div>
            </div>
            <button data-testid="apply-seller-btn" onClick={applySeller} disabled={applying} className="mt-6 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-8 py-3 btn-glow transition-all disabled:opacity-50">
              {applying ? "Verifying..." : "Verify Me & Start Selling"}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <h1 className="font-display text-3xl font-bold mb-2">List an Item 💄</h1>
      <p className="text-neutral-500 mb-8">Snap a few photos, add the details, and set your price — your beauty could be someone's next holy-grail.</p>

      {connect && !connect.onboarded && (
        <div data-testid="payout-setup-banner" className="mb-6 rounded-2xl border border-[#E8956B]/50 bg-[#E8956B]/10 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-neutral-700 font-medium">
            <CreditCard size={18} className="text-[#E8956B]" /> Set up Stripe payouts so you get paid when your items sell.
          </div>
          <button data-testid="onboard-btn" onClick={onboard} className="bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-5 py-2 transition-all">
            {connect.has_account ? "Finish payout setup" : "Set up payouts"}
          </button>
        </div>
      )}
      {connect && connect.onboarded && (
        <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700 font-medium flex items-center gap-2">
          <Check size={16} /> Payouts active — you're all set to receive your 85% when items sell.
        </div>
      )}

      <form onSubmit={submit} className="space-y-5">
        <div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Photos</label>
            <div className="flex items-center gap-2 text-xs">
              <span data-testid="ai-credits-count" className="flex items-center gap-1 text-neutral-600"><Zap size={13} className="text-[#E8956B]" /> {credits} photo boost{credits === 1 ? "" : "s"}</span>
              <button type="button" data-testid="buy-credits-btn" onClick={buyCredits} disabled={buyingCredits} className="text-[#E8956B] font-semibold hover:underline disabled:opacity-50">
                {buyingCredits ? "..." : "Buy (5 for $2.99)"}
              </button>
            </div>
          </div>
          <div data-testid="photo-guidance" className="mt-2 rounded-xl border border-[#E8956B]/30 bg-[#E8956B]/5 p-3">
            <p className="text-xs font-bold text-neutral-700 mb-1">📸 For the best listing, add clear photos of:</p>
            <p className="text-[11px] text-neutral-500 mb-2">Only add what you have — tap "Don't have it" to skip any you're missing. <Link to="/clean-guide" className="text-[#CE744A] font-semibold hover:underline" data-testid="sell-clean-guide-link">See our cleaning guide →</Link></p>
            <ul className="text-[12px] text-neutral-600 space-y-1.5">
              {[
                { key: "front", label: "The front of the product", hint: "the packaging / box" },
                { key: "label", label: "The label with product information", hint: "name, shade, batch/expiry" },
                { key: "product", label: "The actual product itself", hint: "unboxed, so buyers see its true condition" },
              ].map((item) => {
                const skipped = skipPhoto[item.key];
                return (
                  <li key={item.key} className="flex items-start justify-between gap-3">
                    <span className={skipped ? "text-neutral-400 line-through" : ""}>
                      <strong>{item.label}</strong> <span className="text-neutral-500">({item.hint})</span>
                    </span>
                    <button
                      type="button"
                      data-testid={`skip-photo-${item.key}`}
                      onClick={() => setSkipPhoto((s) => ({ ...s, [item.key]: !s[item.key] }))}
                      className={`shrink-0 text-[11px] font-semibold rounded-full px-2.5 py-0.5 border transition-all ${
                        skipped ? "bg-neutral-200 border-neutral-300 text-neutral-600" : "border-[#E8956B]/40 text-[#CE744A] hover:bg-[#E8956B]/10"
                      }`}
                    >
                      {skipped ? "Skipped ✓" : "Don't have it"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {credits > 0 && credits <= 2 && (
            <button type="button" data-testid="low-boost-nudge" onClick={buyCredits} disabled={buyingCredits}
              className="mt-1.5 inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1 text-[11px] font-semibold hover:bg-amber-100 transition-all disabled:opacity-50">
              <Zap size={12} /> {credits === 1 ? "1 boost left" : `Only ${credits} boosts left`} — top up for $2.99
            </button>
          )}
          <p className="text-[11px] text-neutral-400 mt-1">⚡ AI Image Boost: hover a photo and tap Boost to auto-enhance it to studio-quality, high-res imagery (sharpen + lighting). 1 boost each, only charged if you keep it. Unused boosts stay on your account for your next listing.</p>
          <div className="grid grid-cols-4 gap-3 mt-2">
            {images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-neutral-200 group">
                <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setImages(images.filter((_, x) => x !== i))}
                  className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-white hover:text-[#E8956B]">
                  <X size={12} />
                </button>
                <button type="button" data-testid={`enhance-image-${i}`} onClick={() => openEnhance(i)}
                  className="absolute bottom-1 left-1 right-1 bg-[#E8956B]/95 text-white text-[10px] font-bold rounded-full py-1 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Zap size={11} /> Boost
                </button>
              </div>
            ))}
            <label data-testid="image-upload-label" className="aspect-square rounded-xl border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#E8956B] transition-colors text-neutral-400">
              {uploading ? <Loader2 size={20} className="animate-spin" /> : <><Upload size={20} /><span className="text-[10px] mt-1">Add</span></>}
              <input data-testid="image-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
            </label>
          </div>
          {images.length >= 2 && (
            <button type="button" data-testid="boost-all-btn" onClick={runBoostAll} disabled={!!boostAll || credits < 1}
              className="mt-3 w-full flex items-center justify-center gap-2 border-2 border-[#E8956B] text-[#CE744A] hover:bg-[#E8956B]/10 font-bold rounded-full py-2.5 text-sm transition-all disabled:opacity-50">
              {boostAll ? <><Loader2 size={15} className="animate-spin" /> Boosting {boostAll.done}/{boostAll.total}…</>
                : <><Zap size={15} /> Boost all photos ({Math.min(credits, images.length)} boost{Math.min(credits, images.length) === 1 ? "" : "s"})</>}
            </button>
          )}
        </div>

        <Input data-testid="product-title-input" placeholder="Product title" value={form.title} onChange={(e) => set("title", e.target.value)} required className="bg-neutral-50 border-neutral-300 h-11" />
        <Input data-testid="product-brand-input" placeholder="Brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} required className="bg-neutral-50 border-neutral-300 h-11" />
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Describe the makeup</label>
          <Textarea data-testid="product-desc-input" placeholder="Describe the makeup — shade/color, how much it's been used, expiry or PAO, ingredients, why you're selling..." value={form.description} onChange={(e) => set("description", e.target.value)} required className="bg-neutral-50 border-neutral-300 min-h-32 mt-2" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input data-testid="product-price-input" type="number" step="0.01" placeholder="Price ($)" value={form.price} onChange={(e) => set("price", e.target.value)} required className="bg-neutral-50 border-neutral-300 h-11" />
          <Input data-testid="product-saleprice-input" type="number" step="0.01" placeholder="Sale price (optional)" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} className="bg-neutral-50 border-neutral-300 h-11" />
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B] flex items-center gap-1.5">
            Condition — new, swatched once, or lightly used? <span className="text-red-500">*</span>
            <button type="button" data-testid="condition-help-btn" onClick={() => setShowConditionHelp((v) => !v)}
              aria-label="What do these conditions mean?" className="text-neutral-400 hover:text-[#E8956B] transition-colors">
              <HelpCircle size={14} />
            </button>
          </label>
          {showConditionHelp && (
            <div data-testid="condition-help-box" className="mt-2 rounded-xl border border-[#E8956B]/30 bg-[#E8956B]/5 p-3 text-[12px] text-neutral-600 leading-relaxed space-y-1.5">
              <p><b className="text-neutral-800">New</b> — Never used, often still sealed or in original packaging. Basically brand-new.</p>
              <p><b className="text-neutral-800">Swatched once</b> — Applied just one time to test the shade (e.g. a quick swipe on skin), then cleaned. Essentially like-new.</p>
              <p><b className="text-neutral-800">Lightly used</b> — Used a handful of times and gently cared for. Still plenty of product left and in lovely shape.</p>
              <p className="text-neutral-400">Be honest about condition — it builds buyer trust and better reviews. ✨</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            {[
              { value: "new", label: "New", hint: "Never used / sealed" },
              { value: "swatched", label: "Swatched once", hint: "Tested once, like-new" },
              { value: "used", label: "Lightly used", hint: "Gently used, still lovely" },
            ].map((opt) => {
              const active = form.condition === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`product-condition-${opt.value}`}
                  onClick={() => set("condition", opt.value)}
                  className={`flex items-start gap-2.5 rounded-xl border-2 p-3 text-left transition-all ${
                    active ? "border-[#E8956B] bg-[#E8956B]/10" : "border-neutral-300 bg-neutral-50 hover:border-[#E8956B]/60"
                  }`}
                >
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                    active ? "border-[#E8956B] bg-[#E8956B] text-white" : "border-neutral-400 bg-white"
                  }`}>
                    {active && <Check size={13} strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-neutral-800 leading-tight break-words">{opt.label}</span>
                    <span className="block text-[11px] text-neutral-500 leading-tight break-words mt-0.5">{opt.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">How many times has this been used? <span className="text-red-500">*</span></label>
          <Select value={form.times_used} onValueChange={(v) => set("times_used", v)}>
            <SelectTrigger data-testid="product-times-used-select" className="bg-neutral-50 border-neutral-300 h-11 mt-2"><SelectValue placeholder="Select usage" /></SelectTrigger>
            <SelectContent className="bg-white border-neutral-200 text-neutral-900">
              <SelectItem value="Never used">Never used (brand new)</SelectItem>
              <SelectItem value="Swatched once">Swatched once (tested to check the shade)</SelectItem>
              <SelectItem value="1–2 times">Used 1–2 times</SelectItem>
              <SelectItem value="3–5 times">Used 3–5 times</SelectItem>
              <SelectItem value="6+ times">Used 6+ times</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Category <span className="text-red-500">*</span></label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger data-testid="product-category-select" className="bg-neutral-50 border-neutral-300 h-11 mt-2"><SelectValue placeholder="Choose a category" /></SelectTrigger>
            <SelectContent className="bg-white border-neutral-200 text-neutral-900">
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <button data-testid="publish-listing-btn" type="submit" disabled={submitting} className="w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-3.5 btn-glow transition-all disabled:opacity-50">
          {submitting ? "Publishing..." : "Publish Listing"}
        </button>
      </form>

      {enhance && (
        <div data-testid="enhance-modal" className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={() => setEnhance(null)}>
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-bold flex items-center gap-2"><Zap size={18} className="text-[#E8956B]" /> AI Image Boost</h3>
              <button onClick={() => setEnhance(null)} className="text-neutral-400 hover:text-neutral-700"><X size={18} /></button>
            </div>
            <p className="text-xs text-neutral-500 mb-4">We're enhancing your photo to studio-quality, high-resolution imagery — sharpening details and optimizing the lighting. You'll only use a boost if you keep the result.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] font-bold text-neutral-400 uppercase mb-1 text-center">Original</div>
                <div className="aspect-square rounded-xl overflow-hidden border border-neutral-200 bg-neutral-100">
                  <img src={imgUrl(enhance.original)} alt="original" className="w-full h-full object-cover" />
                </div>
              </div>
              <div>
                <div className="text-[11px] font-bold text-[#E8956B] uppercase mb-1 text-center">Boosted</div>
                <div className="aspect-square rounded-xl overflow-hidden border-2 border-[#E8956B]/40 bg-neutral-100 flex items-center justify-center">
                  {enhance.loading ? <div className="flex flex-col items-center gap-2 text-[#E8956B]"><Loader2 className="animate-spin" /><span className="text-[11px] text-neutral-400">Boosting…</span></div>
                    : enhance.enhanced ? <img src={imgUrl(enhance.enhanced)} alt="boosted" className="w-full h-full object-cover" data-testid="enhanced-preview" />
                    : <button data-testid="retry-boost-btn" onClick={() => runBoost(enhance.index)} className="text-xs text-[#E8956B] font-semibold underline px-4">Boost failed — tap to try again</button>}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button data-testid="keep-original-btn" onClick={() => setEnhance(null)} className="flex-1 border-2 border-neutral-200 text-neutral-600 font-bold rounded-full py-2.5 hover:border-neutral-400 transition-all">
                Keep Original
              </button>
              <button data-testid="keep-enhanced-btn" onClick={keepEnhanced} disabled={!enhance.enhanced || enhance.loading}
                className="flex-1 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-2.5 transition-all disabled:opacity-40">
                Keep Boosted (1 boost)
              </button>
            </div>
          </div>
        </div>
      )}

      {published && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="publish-success-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center relative">
            <button onClick={() => { navigate(`/product/${published.id}`); }} className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-700" data-testid="publish-success-close"><X size={18} /></button>
            <div className="w-14 h-14 rounded-full bg-[#E8956B]/15 flex items-center justify-center mx-auto mb-3">
              <Check size={28} className="text-[#E8956B]" strokeWidth={3} />
            </div>
            <h3 className="font-display text-2xl font-bold text-neutral-900">Your listing is live! 🎉</h3>
            <p className="text-sm text-neutral-500 mt-1">One last thing — grab your Seller Hygiene Protocol. Follow it to clean, sanitize &amp; pack your item so buyers leave 5-star reviews.</p>
            <img src={HYGIENE_PROTOCOL_URL} alt="Seller Hygiene Protocol" className="w-40 mx-auto my-4 rounded-lg border border-neutral-200" />
            <a href={HYGIENE_PROTOCOL_URL} download="beauty-trade-club-hygiene-protocol.png" target="_blank" rel="noreferrer"
              data-testid="download-hygiene-btn"
              className="flex items-center justify-center gap-2 w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-3 transition-all">
              <Download size={16} /> Download hygiene protocol
            </a>
            <div className="flex gap-2 mt-3">
              <button onClick={() => navigate(`/product/${published.id}`)} data-testid="view-listing-btn"
                className="flex-1 border-2 border-[#E8956B] text-[#CE744A] hover:bg-[#E8956B]/10 font-bold rounded-full py-2.5 text-sm transition-all">
                View my listing
              </button>
              <button onClick={() => { setPublished(null); setForm({ title: "", brand: "", description: "", price: "", sale_price: "", condition: "", category: "", times_used: "" }); setImages([]); setSkipPhoto({ front: false, label: false, product: false }); }}
                data-testid="list-another-btn"
                className="flex-1 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-bold rounded-full py-2.5 text-sm transition-all">
                List another
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
